import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Court from '../../entities/court';
import Docket from '../../entities/docket';
import Matter from '../../entities/matter';
import CourtDeadlineRule from '../../entities/courtDeadlineRule';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteCourtDeadlineRule, collectCourtDeadlineRuleDeleteImpact} from './courtDeadlineRuleDeps';
export function canDeleteCourt(): boolean {
return canCascadeDelete();
}
export function collectCourtDeleteImpact(court: Court, context: DeleteImpactContext): void {
const key = 'court:' + court.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const docketCourtIdImpact = workingRows<Docket>('docket', findAll<Docket>('dockets')).filter(docketRef => docketRef.courtId === court.id);
docketCourtIdImpact.forEach(docketRef => addDeleteImpact(context, 'docket:' + docketRef.id, 'dockets'));
const matterCourtIdsImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.courtIds?.includes(court.id));
matterCourtIdsImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters'));
const courtParentCourtIdImpact = workingRows<Court>('court', findAll<Court>('courts')).filter(courtRef => courtRef.parentCourtId === court.id);
courtParentCourtIdImpact.forEach(courtRef => addDeleteImpact(context, 'court:' + courtRef.id, 'courts'));workingRows<CourtDeadlineRule>('courtDeadlineRule', findAll<CourtDeadlineRule>('courtDeadlineRules')).filter(courtDeadlineRule => courtDeadlineRule.status !== 'D' && courtDeadlineRule.courtId === court.id).forEach(courtDeadlineRule => {
if (context.visited.has('courtDeadlineRule:' + courtDeadlineRule.id)) return;
addDeleteImpact(context, 'courtDeadlineRule:' + courtDeadlineRule.id, 'courtDeadlineRules');
collectCourtDeadlineRuleDeleteImpact(courtDeadlineRule, context);
});
}
export function deleteImpactCourt(court: Court): DeleteImpact {
const context = deleteImpactContext();
collectCourtDeleteImpact(court, context);
return deleteImpactCounts(context);
}
export function deleteCourt(court: Court, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteCourt()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectCourtDeleteImpact(court, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || court.id};
}
const key = 'court:' + court.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<CourtDeadlineRule>('courtDeadlineRule', findAll<CourtDeadlineRule>('courtDeadlineRules')).filter(courtDeadlineRule => courtDeadlineRule.status !== 'D' && courtDeadlineRule.courtId === court.id).forEach(courtDeadlineRule => {deleteCourtDeadlineRule(courtDeadlineRule, mergeId, cascade);});
workingRows<Docket>('docket', [...store.getState().dockets.getMappingFor('courtId', court.id)]).filter(docketTemp => docketTemp.courtId === court.id).forEach(docketTemp => {
const _docket = ((_work.get('docket:' + docketTemp.id) ?? docketTemp) as Docket).clone();
if (_docket.status === 'D') return;
_docket.status = 'U';
_docket.addedOn = newHRTimestamp();
_docket.addedBy = getCurrentUserId();
_docket.importSource = targetImportSource;
_docket.importId = mergeId;
if (_docket.courtId === court.id) {
_docket.courtId = null;

}
_work.set('docket:' + _docket.id, _docket);
dispatch(Actions.DocketActions.saveDocketQueue(_docket), 'docket:' + _docket.id);
});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('courtId', court.id)]).filter(matterTemp => matterTemp.courtIds?.includes(court.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.courtIds = _matter.courtIds?.filter(id => id !== court.id) || [];

_work.set('matter:' + _matter.id, _matter);
dispatch(Actions.MatterActions.saveMatterQueue(_matter), 'matter:' + _matter.id);
});
workingRows<Court>('court', [...store.getState().courts.getMappingFor('courtId', court.id)]).filter(courtTemp => courtTemp.parentCourtId === court.id).forEach(courtTemp => {
const _court = ((_work.get('court:' + courtTemp.id) ?? courtTemp) as Court).clone();
if (_court.status === 'D') return;
_court.status = 'U';
_court.addedOn = newHRTimestamp();
_court.addedBy = getCurrentUserId();
_court.importSource = targetImportSource;
_court.importId = mergeId;
if (_court.parentCourtId === court.id) {
_court.parentCourtId = null;

}
_work.set('court:' + _court.id, _court);
dispatch(Actions.CourtActions.saveCourtQueue(_court), 'court:' + _court.id);
});
const deleted = court.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.CourtActions.removeCourtQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceCourt(court: Court, replaceWith: Court, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = court.id;}
const _work = workingSet();
workingRows<Docket>('docket', [...store.getState().dockets.getMappingFor('courtId', court.id)]).filter(docketTemp => docketTemp.courtId === court.id).forEach(docketTemp => {
const _docket = ((_work.get('docket:' + docketTemp.id) ?? docketTemp) as Docket).clone();
if (_docket.status === 'D') return;
_docket.status = 'U';
_docket.addedOn = newHRTimestamp();
_docket.addedBy = getCurrentUserId();
_docket.importSource = targetImportSource;
_docket.importId = mergeId;
if (_docket.courtId === court.id) {
_docket.courtId = replaceWith.id;

}
_work.set('docket:' + _docket.id, _docket);
store.dispatch(Actions.DocketActions.saveDocketQueue(_docket));
});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('courtId', court.id)]).filter(matterTemp => matterTemp.courtIds?.includes(court.id)).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
_matter.courtIds = _matter.courtIds?.map(id => (id === court.id) ? replaceWith.id : id) || [];

_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Court>('court', [...store.getState().courts.getMappingFor('courtId', court.id)]).filter(courtTemp => courtTemp.parentCourtId === court.id).forEach(courtTemp => {
const _court = ((_work.get('court:' + courtTemp.id) ?? courtTemp) as Court).clone();
if (_court.status === 'D') return;
_court.status = 'U';
_court.addedOn = newHRTimestamp();
_court.addedBy = getCurrentUserId();
_court.importSource = targetImportSource;
_court.importId = mergeId;
if (_court.parentCourtId === court.id) {
_court.parentCourtId = replaceWith.id;

}
_work.set('court:' + _court.id, _court);
store.dispatch(Actions.CourtActions.saveCourtQueue(_court));
});



workingRows<CourtDeadlineRule>('courtDeadlineRule', findAll<CourtDeadlineRule>('courtDeadlineRules')).filter(courtDeadlineRule => courtDeadlineRule.status !== 'D' && courtDeadlineRule.courtId === court.id).forEach(courtDeadlineRuleTemp => {
const _courtDeadlineRule = ((_work.get('courtDeadlineRule:' + courtDeadlineRuleTemp.id) ?? courtDeadlineRuleTemp) as CourtDeadlineRule).clone();
if (_courtDeadlineRule.status === 'D') return;
_courtDeadlineRule.courtId = replaceWith.id;
_courtDeadlineRule.status = 'U';
_courtDeadlineRule.addedOn = newHRTimestamp();
_courtDeadlineRule.addedBy = getCurrentUserId();
_courtDeadlineRule.importSource = targetImportSource;
_courtDeadlineRule.importId = mergeId;
_work.set('courtDeadlineRule:' + _courtDeadlineRule.id, _courtDeadlineRule);
store.dispatch(Actions.CourtDeadlineRuleActions.saveCourtDeadlineRuleQueue(_courtDeadlineRule));
});
return court;
}
